import { useLoadMore } from '@react-aria/utils';
import { clsx } from 'clsx';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { SortDescriptor, TableLayout, Virtualizer } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Button from '~/components/buttons/NewButton';
import { Table, TableBody, TableColumn, TableHeader } from '~/components/data-display/Table/Table';
import { useDefaultRanges } from '~/components/date-pickers/DateRangePicker/DefinedRanges';
import AmountFilter from '~/components/filters/AmountFilter';
import BookkeepingAdjustmentGroupFilter from '~/components/filters/BookkeepingAdjustmentGroupFilter';
import InlineSearchFilter from '~/components/filters/InlineSearchFilter';
import { PresetDate } from '~/components/inputs/DatePicker/CalendarRangePopover';
import DateRangePicker from '~/components/inputs/DatePicker/DateRangePicker';
import { HStack } from '~/components/layout/Stack';
import ConfirmationModal from '~/components/modals/ConfirmationModal/ConfirmationModal';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import IconDelete from '~/icons/icon-delete.svg?react';
import IconRefresh from '~/icons/icon-refresh.svg?react';
import { useModal } from '~/modals/Modal';
import { RouteConfig } from '~/routes/routeConfig';
import { useAllBookkeepingAccountsQuery } from '~/services/bookkeeping/bookkeeping-accounts';
import {
  useBookkeepingTransactions,
  useDeleteBookkeepingTransaction,
} from '~/services/bookkeeping/bookkeeping-transactions';
import { useVATCodesQuery } from '~/services/bookkeeping/vat-codes';
import { useDeleteVATRegistration } from '~/services/bookkeeping/vat-registrations';
import { useRtDeviceSettingsMap } from '~/services/rt-device-settings';
import { useSorting, useSetSortingWithDirection } from '~/state/tableSorting';
import { DocumentType } from '~/types/document';
import { BookkeepingTransaction, BookkeepingTransactionSorting } from '~/types/general-bookkeeping';
import { isDefined } from '~/types/typeGuards';
import { useFormatRange } from '~/utils/date';
import { useOpenSingleRegistrationAsideWithBookkeepingTransaction } from '~/views/bookkeeping/RegistrationAside/SingleRegistrationAsideState';
import { REDIRECT_TO_QUERY_PARAM } from '~/views/bookkeeping/bookkeeping-constants';
import GeneralBookkeepingCashBookTableRow from '~/views/bookkeeping/cash-book/GeneralBookkeepingCashBookTableRow';
import {
  getScrollPosition,
  useAdjustmentGroupsFilter,
  useCashBookAmountRangeFilter,
  useDateFilter,
  useHasActiveCashBookFilters,
  useResetCashBookFilters,
  useSearchFilter,
  useSetScrollPosition,
} from '~/views/bookkeeping/cash-book/cash-book-state';
import useGeneralBookkeepingCashBookQueryOptions from '~/views/bookkeeping/cash-book/hooks/useGeneralBookkeepingCashBookQueryOptions';
import styles from './GeneralBookkeepingCashBookTable.module.scss';

const GeneralBookkeepingCashBookTable: React.FC = () => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'general_bookkeeping.cash_book' });
  const { t: tDeleteVatRegistration } = useTranslation('bookkeeping', {
    keyPrefix: 'registrations.delete_vat_registration',
  });
  // Eagerly warm the cache for the detail tooltip
  useVATCodesQuery();
  useAllBookkeepingAccountsQuery({ hasChildren: false });

  const deviceSettingsMap = useRtDeviceSettingsMap();
  const [dateRange, setDateRange] = useDateFilter();
  const [search, setSearch] = useSearchFilter();
  const [cashBookAmountRange, setCashBookAmountRange] = useCashBookAmountRangeFilter();
  const [adjustmentGroups, setAdjustmentGroups] = useAdjustmentGroupsFilter();
  const hasActiveFilters = useHasActiveCashBookFilters();
  const resetFilters = useResetCashBookFilters();
  const filters = useGeneralBookkeepingCashBookQueryOptions();
  const sorting = useSorting<BookkeepingTransactionSorting>();
  const setSortingWithDirection = useSetSortingWithDirection();
  const { mutate: deleteTransaction } = useDeleteBookkeepingTransaction();
  const { mutate: deleteVATRegistration } = useDeleteVATRegistration();
  const openAsideWithBookkeepingTransaction = useOpenSingleRegistrationAsideWithBookkeepingTransaction();

  const sortDescriptor: SortDescriptor = useMemo(
    () => ({
      column: sorting.sort,
      direction: sorting.direction === 'asc' ? 'ascending' : 'descending',
    }),
    [sorting],
  );

  const onSortChange = useCallback(
    (descriptor: SortDescriptor) => {
      if (typeof descriptor.column === 'number') return;
      setSortingWithDirection({
        sort: descriptor.column,
        direction: descriptor.direction === 'ascending' ? 'asc' : 'desc',
      });
    },
    [setSortingWithDirection],
  );

  const { data, isFetching, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useBookkeepingTransactions(
    filters,
    sorting,
  );

  const allTransactions = useMemo(() => data?.pages.flatMap((page) => page.transactions) ?? [], [data]);

  const isTableLoading = isFetching || isLoading || isFetchingNextPage;

  const defaultRanges = useDefaultRanges(new Date(), false);
  const formatRange = useFormatRange();

  const { showModal } = useModal();
  const navigate = useNavigate();

  const presetDates: PresetDate[] = useMemo(
    () =>
      defaultRanges.map((range, index) => ({
        id: `preset-${index}`,
        label: formatRange(range.startDate, range.endDate),
        date: {
          start: range.startDate!,
          end: range.endDate!,
        },
      })),
    [defaultRanges, formatRange],
  );

  const scrollRef = useRef<HTMLTableElement>(null);
  useLoadMore(
    {
      isLoading: isTableLoading,
      onLoadMore: hasNextPage ? fetchNextPage : undefined,
      scrollOffset: 0.9,
      items: data,
    },
    scrollRef,
  );

  const setScrollPosition = useSetScrollPosition();

  useEffect(() => {
    if (!scrollRef.current || isLoading || isEmpty(allTransactions)) return;

    const scrollContainer = scrollRef.current;
    const scrollPosition = getScrollPosition();

    if (scrollPosition?.scrollTop && isEqual(scrollPosition.filters, filters)) {
      const scrollId = requestAnimationFrame(() => {
        scrollContainer.scrollTop = scrollPosition.scrollTop;
      });

      return () => cancelAnimationFrame(scrollId);
    } else {
      scrollContainer.scrollTop = 0;
    }
  }, [isLoading, allTransactions, filters]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLTableElement>) => {
      setScrollPosition({
        scrollTop: e.currentTarget.scrollTop,
        filters,
      });
    },
    [setScrollPosition, filters],
  );

  const handleEditTransaction = useCallback(
    (transaction: BookkeepingTransaction) => {
      if (!isDefined(transaction.vatRegistration)) {
        openAsideWithBookkeepingTransaction(transaction);
        return;
      }

      let route;
      const document = transaction.vatRegistration.documents[0];
      const search = new URLSearchParams({
        [REDIRECT_TO_QUERY_PARAM]: RouteConfig.GeneralBookkeepingCashBook.buildLink(),
      }).toString();

      if (isDefined(document)) {
        const { id: documentId, documentType } = document;
        route =
          documentType === DocumentType.Bill
            ? RouteConfig.BookkeepingEditBillVATRegistration.buildLink({ documentId })
            : RouteConfig.BookkeepingEditVATRegistration.buildLink({ documentId });
      } else {
        route = RouteConfig.BookkeepingEditManualVATRegistration.buildLink({
          registrationId: transaction.vatRegistration.id,
        });
      }
      navigate(`${route}?${search}`);
    },
    [navigate, openAsideWithBookkeepingTransaction],
  );

  const handleDeleteTransaction = useCallback(
    (transaction: BookkeepingTransaction) => {
      let onConfirm: () => void;
      const onSuccess = () => snack.success(t('delete_transaction.success'));
      const title = t('delete_transaction.title');
      let confirmLabel = t('delete_transaction.confirm_label');
      let description = isEmpty(transaction.transactions)
        ? t('delete_transaction.description_permanent')
        : t('delete_transaction.description');

      const vatRegistration = transaction.vatRegistration;
      if (isDefined(vatRegistration)) {
        const hasDocument = isDefined(vatRegistration.documents[0]);
        confirmLabel = tDeleteVatRegistration('confirm_label');
        description = tDeleteVatRegistration(hasDocument ? 'description' : 'description_manual');
        onConfirm = () => deleteVATRegistration(vatRegistration, { onSuccess });
      } else {
        onConfirm = () => deleteTransaction(transaction, { onSuccess });
      }

      showModal({
        component: ConfirmationModal,
        props: { action: 'destructive', confirmLabel, description, Icon: IconDelete, onConfirm, title },
      });
    },
    [deleteTransaction, deleteVATRegistration, showModal, t, tDeleteVatRegistration],
  );

  return (
    <>
      <HStack align="end" gap={2} className={styles.filtersContainer}>
        <InlineSearchFilter name="search" value={search} onChange={(_, value) => setSearch(value)} />
        <DateRangePicker
          value={dateRange}
          onChange={setDateRange}
          label={t('filters.date_label')}
          placeholder={t('filters.date_placeholder')}
          presetDates={presetDates}
          clearable
        />
        <AmountFilter
          value={cashBookAmountRange}
          name="cashBookAmountRange"
          onChange={(_, value) => setCashBookAmountRange(value)}
          label={t('filters.amount_label')}
          placeholder={t('filters.amount_placeholder')}
        />
        <BookkeepingAdjustmentGroupFilter value={adjustmentGroups} onChange={setAdjustmentGroups} />
        {hasActiveFilters && (
          <Button onClick={resetFilters} Icon={IconRefresh} variant="ghost" size="medium">
            <Text variant="body2" weight="semibold">
              {t('filters.reset')}
            </Text>
          </Button>
        )}
      </HStack>
      <div className={styles.tableWrapper}>
        <Virtualizer layout={TableLayout} layoutOptions={layoutOptions}>
          <Table
            className={clsx(styles.table)}
            ref={scrollRef}
            aria-label={t('title')}
            sortDescriptor={sortDescriptor}
            onSortChange={onSortChange}
            onScroll={handleScroll}
          >
            <TableHeader>
              <TableColumn id="date" width={120} allowsSorting>
                {t('table.date_registration')}
              </TableColumn>
              <TableColumn width={70}>{t('table.protocol')}</TableColumn>
              <TableColumn isRowHeader minWidth={340}>
                {t('table.description')}
              </TableColumn>
              <TableColumn minWidth={120}>{t('table.document_number')}</TableColumn>
              <TableColumn minWidth={120}>{t('table.date_document')}</TableColumn>
              <TableColumn minWidth={260}>{t('table.counterpart')}</TableColumn>
              <TableColumn align="right" minWidth={125}>
                {t('table.amount')}
              </TableColumn>
              <TableColumn align="center" width={100}>
                {t('table.details')}
              </TableColumn>
              <TableColumn align="right" width={100} />
            </TableHeader>
            <TableBody items={allTransactions} dependencies={[allTransactions]}>
              {(item) => (
                <GeneralBookkeepingCashBookTableRow
                  key={item.id}
                  transaction={item}
                  deviceSettingsMap={deviceSettingsMap}
                  onEdit={handleEditTransaction}
                  onDelete={handleDeleteTransaction}
                />
              )}
            </TableBody>
          </Table>
        </Virtualizer>
      </div>
    </>
  );
};

const layoutOptions = { rowHeight: 72, headingHeight: 34 };

export default GeneralBookkeepingCashBookTable;
